import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Grid,
  Typography,
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
} from "@mui/material";
import api from '../config/api';
import {
  ref,
  getBlob,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { storage } from "../config/firebaseConfig";
import useStore from "../config/store";
import { useNavigate, useParams } from "react-router-dom";
import MakerStepper from "./MakerStepper";
import { useTranslation } from 'react-i18next';

export default function MakerLayout() {
  const { user, authToken, showAlert, project, organization, language } = useStore.getState();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reportId } = useParams();

  // REF para controlar el polling
  const pollingIntervalRef = useRef(null);
  const isPollingActiveRef = useRef(false);
  const currentReportIdRef = useRef(null);

  // Definición de pasos
  const pasos = ['step1', 'step2', 'step3', 'step4', 'step5'].map(step => ({
    title: t(`reports.steps.${step}.title`),
    description: t(`reports.steps.${step}.description`)
  }));

  const pasoFinal = {
    title: t('reports.layout.reportTitle'),
    description: t('reports.layout.reportSubTitle')
  };

  // Estados del reporte
  const [activeReport, setActiveReport] = useState(null);
  const [gptTask, setGptTask] = useState(null);
  const [gptProcessing, setGptProcessing] = useState(false);
  const [refreshReportList, setRefreshReportList] = useState(0);
  const [sending, setSending] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);

  // Estados de datos del formulario
  const [contexts, setContexts] = useState(() => pasos.map(() => ""));
  const [localFiles, setLocalFiles] = useState(() => pasos.map(() => []));
  const [skipped, setSkipped] = useState(() => pasos.map(() => false));
  const [dynamicSteps, setDynamicSteps] = useState([]);
  const [candidateData, setCandidateData] = useState({
    name: "",
    email: "",
    applicant: null,
  });

  // Estados para positions
  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const [usageCheck, setUsageCheck] = useState(null);
  const [loadingUsageCheck, setLoadingUsageCheck] = useState(true);

  // Función para verificar si un estado es final
  const isInFinalState = useCallback((status) => {
    const finalStates = [
      "completado",
      "completed",
      "error",
      "cancelado",
      "fallido",
      "cancelled",
      "failed",
    ];
    return finalStates.includes(status?.toLowerCase());
  }, []);

  // Función para detener el polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingActiveRef.current = false;
    setGptProcessing(false);
  }, []);

  // Función para iniciar el polling
  const startPolling = useCallback(
    (reportIdToWatch) => {
      if (
        isPollingActiveRef.current &&
        currentReportIdRef.current === reportIdToWatch
      ) {
        return;
      }
      stopPolling();
      currentReportIdRef.current = reportIdToWatch;
      isPollingActiveRef.current = true;
      setGptProcessing(true);

      const interval = setInterval(async () => {
        try {
          if (
            !isPollingActiveRef.current ||
            currentReportIdRef.current !== reportIdToWatch
          ) {
            stopPolling();
            return;
          }

          const { data } = await api.get(`/reports/${reportIdToWatch}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const report = data.data;

          setActiveReport((prevReport) => {
            if (
              !prevReport ||
              prevReport.status !== report.status ||
              prevReport.report_url !== report.report_url ||
              prevReport.word_document_url !== report.word_document_url
            ) {
              return report;
            }
            return prevReport;
          });

          if (isInFinalState(report.status)) {
            try {
              const { data: finalResp } = await api.get(
                `/reports/${reportIdToWatch}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
              );
              setActiveReport(finalResp.data);
            } catch (err) {
              console.error("❌ Error al obtener reporte final:", err);
            } finally {
              stopPolling();
            }
            return;
          }

          if (gptTask?.id) {
            const taskResult = await api.get(
              `/gpt-agent/tasks/${gptTask.id}`,
              {
                headers: { Authorization: `Bearer ${authToken}` },
              }
            );
            const taskData = taskResult.data.data;

            setGptTask((prevTask) => {
              if (!prevTask || prevTask.status !== taskData.status) {
                return taskData;
              }
              return prevTask;
            });

            if (isInFinalState(taskData.status)) {
              try {
                const { data: finalTaskResp } = await api.get(
                  `/gpt-agent/tasks/${gptTask.id}`,
                  { headers: { Authorization: `Bearer ${authToken}` } }
                );
                setGptTask(finalTaskResp.data.data);
              } catch (err) {
                console.error("❌ Error al obtener tarea GPT final:", err);
              } finally {
                stopPolling();
              }
              return;
            }
          }
        } catch (error) {
          console.error("❌ Error en el polling:", error);
        }
      }, 5000);

      pollingIntervalRef.current = interval;
    },
    [authToken, gptTask?.id, isInFinalState, stopPolling]
  );

  // Función para resetear todos los estados
  const resetAllStates = useCallback(() => {
    stopPolling();
    setContexts(pasos.map(() => ""));
    setLocalFiles(pasos.map(() => []));
    setSkipped(pasos.map(() => false));
    setSending(false);
    setUploadedCount(0);
    setActiveReport(null);
    setGptTask(null);
    setGptProcessing(false);
    setDynamicSteps([]);
    setSelectedPosition(null);
    setCandidateData({
      name: "",
      email: "",
      applicant: null,
    });
    currentReportIdRef.current = null;
  }, [stopPolling]);

  // Función para verificar límites de uso
  const checkUsageLimits = async () => {
    if (!organization || !authToken) return;

    try {
      setLoadingUsageCheck(true);
      const { data } = await api.get(
        `/organizations/${organization}/usage/check`,
        {
          params: { action: 'psycholaborEvaluations' },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (data.response && data.response.status_response === "OK") {
        setUsageCheck(data.response);
      } else {
        console.error("Error al verificar límites de uso:", data);
        setUsageCheck({ canPerform: false }); // En caso de error, permitir uso
      }
    } catch (error) {
      console.error("Error al verificar límites de uso:", error);
      setUsageCheck({ canPerform: true }); // En caso de error, permitir uso
    } finally {
      setLoadingUsageCheck(false);
    }
  };

  // Ajustar arrays cuando cambian dynamicSteps
  useEffect(() => {
    const totalSteps = pasos.length + dynamicSteps.length;
    const newLocalFiles = [...localFiles];
    const newContexts = [...contexts];
    const newSkipped = [...skipped];

    while (newLocalFiles.length < totalSteps) newLocalFiles.push([]);
    while (newContexts.length < totalSteps) newContexts.push("");
    while (newSkipped.length < totalSteps) newSkipped.push(false);

    if (newLocalFiles.length > totalSteps) newLocalFiles.length = totalSteps;
    if (newContexts.length > totalSteps) newContexts.length = totalSteps;
    if (newSkipped.length > totalSteps) newSkipped.length = totalSteps;

    setLocalFiles(newLocalFiles);
    setContexts(newContexts);
    setSkipped(newSkipped);
  }, [dynamicSteps.length]);

  // Cargar positions al montar
  const loadPositions = async () => {
    try {
      setLoadingPositions(true);
      const { data } = await api.get("/positions", {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (data.response && data.response.status_response === "OK") {
        setPositions(data.response.data || []);
      } else {
        console.error("Error al cargar positions:", data);
        showAlert(t('positions.loadError'), "warning");
      }
    } catch (error) {
      console.error("Error al cargar positions:", error);
      showAlert(t('positions.loadError'));
    } finally {
      setLoadingPositions(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      loadPositions();
    }
  }, [authToken]);

  // Lógica que reparte archivos en localFiles según stepIndex
  const cargarArchivosPorStep = (files) => {
    const totalSteps = pasos.length + dynamicSteps.length;
    const organizados = Array(totalSteps)
      .fill()
      .map(() => []);

    files.forEach((file) => {
      if (
        file.stepIndex != null &&
        file.stepIndex >= 0 &&
        file.stepIndex < totalSteps
      ) {
        organizados[file.stepIndex].push(file);
      }
    });

    setLocalFiles(organizados);
  };

  // Obtener un reporte individual
  const fetchOne = async (id) => {
    try {
      const { data } = await api.get(`/reports/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const reportData = data.data;
      setActiveReport(reportData);

      if (reportData.dynamicSteps && Array.isArray(reportData.dynamicSteps)) {
        setDynamicSteps(reportData.dynamicSteps);
      }

      if (reportData.position_id) {
        const selectedPos = positions.find(
          (pos) =>
            pos.id === reportData.position_id ||
            pos.uid === reportData.position_id
        );
        if (selectedPos) {
          setSelectedPosition(selectedPos);
        } else {
          try {
            const { data: posData } = await api.get(
              `/positions/${reportData.position_id}`,
              {
                headers: { Authorization: `Bearer ${authToken}` },
              }
            );
            if (posData.response && posData.response.status_response === "OK") {
              setSelectedPosition(posData.response.data);
            }
          } catch (error) {
            console.error("Error al cargar position individual:", error);
          }
        }
      }

      if (reportData.contexts && Array.isArray(reportData.contexts)) {
        setContexts(reportData.contexts);
      } else {
        const totalSteps =
          pasos.length + (reportData.dynamicSteps?.length || 0);
        setContexts(Array(totalSteps).fill(""));
      }

      if (reportData.skipped && Array.isArray(reportData.skipped)) {
        setSkipped(reportData.skipped);
      } else {
        const totalSteps =
          pasos.length + (reportData.dynamicSteps?.length || 0);
        setSkipped(Array(totalSteps).fill(false));
      }

      if (reportData.applicant_id) {
        try {
          const applicantResp = await api.get(
            `/applicants/${reportData.applicant_id}`,
            {
              headers: { Authorization: `Bearer ${authToken}` },
            }
          );
          const applicantData = applicantResp.data.response.data;
          setCandidateData({
            name: applicantData.name || "",
            rut: applicantData.rut || "",
            applicant: applicantData,
          });
        } catch (error) {
          console.error("Error al cargar datos del candidato:", error);
        }
      } else {
        setCandidateData({
          name: "",
          rut: "",
          applicant: null,
        });
      }

      cargarArchivosPorStep(reportData.files || []);
      await fetchGptTasks(id);
    } catch (error) {
      console.error("❌ Error al obtener reporte:", error);
    }
  };

  // Obtener tareas GPT para un reporte
  const fetchGptTasks = async (reportId) => {
    try {
      const { data } = await api.get(`/gpt-agent/reports/${reportId}/tasks`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (data.data && data.data.length > 0) {
        const latestTask = data.data.sort(
          (a, b) => b.created_at?._seconds - a.created_at?._seconds
        )[0];
        setGptTask(latestTask);
        if (!isInFinalState(latestTask.status)) {
          startPolling(reportId);
        } else {
          stopPolling();
        }
        return latestTask;
      } else {
        stopPolling();
      }
      return null;
    } catch (error) {
      console.error("❌ Error al obtener tareas de GPT:", error);
      throw error;
    }
  };

  // Efectos
  useEffect(() => {
    if (!reportId) {
      resetAllStates();
    }
  }, [reportId, resetAllStates]);

  useEffect(() => {
    if (!user?.uid) {
      console.log("👤 Usuario no disponible, esperando...");
      return;
    }

    if (reportId) {
      console.log(`🎯 Cargando reporte ${reportId}...`);
      fetchOne(reportId);
    } else {
      console.log("🆕 Modo creación de nuevo reporte");
    }

    return () => {
      stopPolling();
    };
  }, [user?.uid, reportId]);

  useEffect(() => {
    if (gptTask && isInFinalState(gptTask.status)) {
      stopPolling();
    }
  }, [gptTask?.status, isInFinalState, stopPolling]);

  // Verificar límites de uso al cargar el componente
  useEffect(() => {
    if (organization && authToken) {
      checkUsageLimits();
    }
  }, [organization]);

  // Cálculos
  const totalFiles = useMemo(
    () => localFiles.reduce((s, a) => s + a.length, 0),
    [localFiles]
  );
  const progressPercent = totalFiles
    ? Math.round((uploadedCount / totalFiles) * 100)
    : 0;
  const allStepsCount = pasos.length + dynamicSteps.length;
  const allDone = Array.from({ length: allStepsCount }, (_, i) => {
    if (i === 0) {
      const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      };
      const hasCandidateData =
        candidateData.applicant !== null ||
        (candidateData.name.trim() !== "" &&
          candidateData.email.trim() !== "" &&
          isValidEmail(candidateData.email.trim()));
      const hasFilesOrContext =
        localFiles[0]?.length > 0 ||
        (contexts[0] && contexts[0].trim() !== "") ||
        skipped[i];
      return hasCandidateData && hasFilesOrContext;
    }
    if (i === 1) {
      return selectedPosition !== null;
    }
    return (
      localFiles[i]?.length > 0 ||
      (contexts[i] && contexts[i].trim() !== "") ||
      skipped[i]
    );
  }).every((x) => x);

  // Handlers para el stepper
  const handleSelectReport = (r) => {
    navigate(`/reports/${r.id}`);
  };

  const handlePositionChange = (event, newValue) => {
    setSelectedPosition(newValue);
    if (reportId) {
      const delayDebounceFn = setTimeout(() => {
        updateSelectedPosition(newValue);
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  };

  const handleContextChange = (step, value) => {
    setContexts((prev) => {
      const copy = [...prev];
      copy[step] = value;
      return copy;
    });
    if (reportId) {
      const delayDebounceFn = setTimeout(() => {
        updateContext(step, value);
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  };

  const handleFilesChange = (step, files, replace = false) => {
    setLocalFiles((prev) => {
      const copy = prev.map((a) => [...a]);
      if (replace) {
        copy[step] = files;
      } else {
        copy[step].push(...files);
      }
      return copy;
    });
  };

  const handleSkippedChange = (newSkipped) => {
    setSkipped(newSkipped);
    if (reportId) {
      updateSkippedStatus(newSkipped);
    }
  };

  const handleDynamicStepsChange = (newDynamicSteps) => {
    setDynamicSteps(newDynamicSteps);
    if (reportId) {
      const delayDebounceFn = setTimeout(() => {
        updateDynamicSteps(newDynamicSteps);
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  };

  // Funciones de actualización de APIs
  const updateSelectedPosition = async (position) => {
    if (!reportId) return;
    try {
      const updateData = {
        position_id: position ? position.id || position.uid : null,
        position_name: position ? position.name : null,
      };
      await api.patch(`/reports/${reportId}/update`, updateData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch (error) {
      console.error("Error al actualizar posición:", error);
    }
  };

  const updateContext = async (step, value) => {
    try {
      const newContexts = [...contexts];
      newContexts[step] = value;
      await api.patch(
        `/reports/${reportId}/update`,
        { contexts: newContexts },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
    } catch (error) { }
  };

  const updateSkippedStatus = async (newSkipped) => {
    try {
      await api.patch(
        `/reports/${reportId}/update`,
        { skipped: newSkipped },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
    } catch (error) { }
  };

  const updateDynamicSteps = async (newDynamicSteps) => {
    try {
      await api.patch(
        `/reports/${reportId}/update`,
        { dynamicSteps: newDynamicSteps },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
    } catch (error) { }
  };

  // Función para crear reporte
  const handleCreateReport = async (selectedNarrative) => {
    if (sending || !allDone) return;

    if (!selectedNarrative?.id || !selectedNarrative?.content?.trim()) {
      showAlert("Debes seleccionar una narrativa para generar el informe", "warning");
      return;
    }

    if (!candidateData.name || !candidateData.email) {
      showAlert(t('reports.layout.emailAndNameRequired'), "warning");
      return;
    }

    setSending(true);
    setUploadedCount(0);
    try {
      const payload = {
        uid: user.uid,
        email: user.email,
        contexts,
        skipped,
        status: t('created'),
        projectId: project,
        organizationId: organization,
        dynamicSteps,
        language,
        selectedNarrative: {
          id: selectedNarrative.id,
          name: selectedNarrative.name || "",
          description: selectedNarrative.description || "",
          content: selectedNarrative.content.trim(),
        },
      };

      if (selectedPosition) {
        payload.position_id = selectedPosition.id || selectedPosition.uid;
        payload.position_name = selectedPosition.name;
      }

      if (candidateData.name) {
        payload.applicant_name = candidateData.name;
      }
      if (candidateData.email) {
        payload.applicant_email = candidateData.email;
      }
      if (candidateData.applicant && candidateData.applicant.id) {
        payload.applicant_id = candidateData.applicant.id;
      }

      const { data: created } = await api.post("/reports", payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const newReport = created.data;

      await api.patch(
        `/reports/${newReport.id}/update-status`,
        { status: t('reports.layout.uploadingFiles') },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      let counter = 0;
      const totalFilesToUpload = localFiles.flat().length;

      for (let step = 0; step < localFiles.length; step++) {
        for (let file of localFiles[step]) {
          try {
            let fileUrl;

            // Si ya viene con URL (p.ej. DISC), solo adjunta
            if (file.url) {
              await api.patch(
                `/reports/${newReport.id}/add-file`,
                {
                  file: {
                    name: file.name,
                    type: file.type,
                    url: file.url,
                    stepIndex: step,
                    context: contexts[step] || "",
                  },
                },
                { headers: { Authorization: `Bearer ${authToken}` } }
              );

              counter++;
              setUploadedCount(counter);
              continue;
            }

            // ===== Subida directa + reintentos cortos =====
            const maxRetries = 4;
            let attempt = 0;
            let uploaded = false;
            let lastError = null;

            while (!uploaded && attempt < maxRetries) {
              attempt++;
              try {
                const originalName = file?.name || "file";
                const safeName = originalName.replace(/\s+/g, "_").replace(/[^\w.\-]/g, "");
                const path = `uploads/${Date.now()}_${safeName}`; // 👈 SIN subcarpetas
                const storageRef = ref(storage, path);

                // 👇 Subida directa (no-resumable). Evita sesiones bugueadas.
                await uploadBytes(storageRef, file, {
                  contentType: file.type || "application/pdf",
                });

                fileUrl = await getDownloadURL(storageRef);

                await api.patch(
                  `/reports/${newReport.id}/add-file`,
                  {
                    file: {
                      name: file.name,
                      type: file.type,
                      url: fileUrl,
                      stepIndex: step,
                      context: contexts[step] || "",
                    },
                  },
                  { headers: { Authorization: `Bearer ${authToken}` } }
                );

                uploaded = true;
                counter++;
                setUploadedCount(counter);

                // Pequeña pausa entre archivos (200–300ms)
                await new Promise(r => setTimeout(r, 250));

              } catch (err) {
                lastError = err;
                // Si no es error transitorio o ya agotamos intentos, revienta
                const status = err?.serverResponseStatus || err?.status;
                const transient = [500, 502, 503, 504].includes(status);
                if (!transient || attempt === maxRetries) throw err;
                // Backoff corto con jitter
                const delay = 400 * attempt + Math.floor(Math.random() * 250);
                await new Promise(r => setTimeout(r, delay));
              }
            }

            // Status progresivo
            if (counter < totalFilesToUpload) {
              await api.patch(
                `/reports/${newReport.id}/update-status`,
                {
                  status: t("reports.layout.uploadingFilesCount", {
                    count: `${counter}/${totalFilesToUpload}`,
                  }),
                },
                { headers: { Authorization: `Bearer ${authToken}` } }
              );
            }

          } catch (error) {
            console.error("❌ Error subiendo archivo:", error);
            throw error;
          }
        }
      }

      await api.patch(
        `/reports/${newReport.id}/update-status`,
        { status: t('reports.layout.uploadFilesSuccess') },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      await createGptTask(newReport.id);

      setActiveReport(newReport);
      setSending(false);
      navigate(`/reports/${newReport.id}`);
      setRefreshReportList((prev) => prev + 1);
    } catch (error) {
      setSending(false);
      if (error.response?.status === 409 && error.response?.data?.status_response === "LIMIT_EXCEEDED") {
        // Es un error de límite de uso
        const usageData = error.response.data.data;

        // Mostrar mensaje específico al usuario
        showAlert(t('reports.usageLimitExceeded', {
          currentUsage: usageData.currentUsage,
          limit: usageData.limit
        }), 'error');

      } else if (error.response?.status === 400) {
        // Es un error de validación normal (campos faltantes, etc.)
        console.error("Error de validación:", error.response.data.message);
        const errorMessage = error.response.data.message || t('reports.validationError');
        showAlert(errorMessage, 'error');

      } else {
        // Otros errores (500, network, etc.)
        console.error("Error al crear reporte:", error);
        showAlert(t('reports.unexpectedError'), 'error');
      }
    }
  };

  const createGptTask = async (reportId) => {
    try {
      const { data } = await api.post(
        "/gpt-agent/tasks",
        {
          reportId,
          instructions:
            "Genera un informe detallado siguiendo la estructura proporcionada, incluyendo emojis, formato profesional y evaluación objetiva del candidato.",
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      setGptTask(data.data);
      await processGptTask(data.data.id);
      return data.data;
    } catch (error) { }
  };

  const processGptTask = async (taskId) => {
    try {
      await api.post(
        `/gpt-agent/tasks/${taskId}/process`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
    } catch (error) {
      console.log(error);
    }
  };

  const retryGptTask = async (taskId) => {
    try {
      await api.post(
        `/gpt-agent/tasks/${taskId}/retry`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (activeReport?.id) {
        startPolling(activeReport.id);
      }
    } catch (error) { }
  };

  // Funciones de descarga
  const downloadFile = async (fileUrl, fileName) => {
    if (!fileUrl) {
      alert(t('reports.layout.fileOrUrlNotAvailable'));
      return;
    }
    try {
      const path = extractPathFromUrl(fileUrl);
      const fileRef = ref(storage, path);
      const blob = await getBlob(fileRef);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || t('file');
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      if (error.code === "storage/unauthorized") {
        console.log(error);
        alert(t('generalApiError'));
      } else {
        alert(t('generalApiError'));
      }
    }
  };

  const extractPathFromUrl = (url) => {
    try {
      const matches = url.match(/\/o\/(.+?)(\?|$)/);
      if (matches && matches[1]) {
        return decodeURIComponent(matches[1]);
      }
      const pathWithoutQueryParams = url.split("?")[0];
      if (
        pathWithoutQueryParams.startsWith("uploads/") ||
        pathWithoutQueryParams.startsWith("reports/")
      ) {
        return pathWithoutQueryParams;
      }
      return url;
    } catch (error) {
      return url;
    }
  };

  const downloadWordDocument = (url, fileName = "informe.docx") => {
    if (!url) {
      alert(t('reports.layout.wordNotAvailable'));
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  };

  const handleRefreshPositions = async () => {
    try {
      setLoadingPositions(true);
      const { data } = await api.get("/positions", {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (data.response && data.response.status_response === "OK") {
        setPositions(data.response.data || []);
      }
    } catch (error) {
      console.error("Error al recargar positions:", error);
    } finally {
      setLoadingPositions(false);
    }
  };

  // Renderizar mensaje de límite alcanzado
  const renderUsageLimitMessage = () => {
    if (!usageCheck) return null;
    const limitText = usageCheck.limit === -1 ? t('reports.layout.unlimited') : usageCheck.limit;

    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Alert severity="warning">
          <AlertTitle>{t('reports.layout.usageLimitReached')}</AlertTitle>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('reports.layout.usageLimitMessage')}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('reports.layout.currentUsage')}:</strong> {usageCheck.currentUsage} / {limitText} {t('reports.layout.evaluations')}
            </Typography>
            {usageCheck.remaining !== undefined && usageCheck.remaining >= 0 && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t('reports.layout.remainingEvaluations')}:</strong> {usageCheck.remaining}
              </Typography>
            )}
          </Box>
        </Alert>
      </Box>
    );
  };

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12} md={12}>
        <Typography variant="h6" gutterBottom>
          {t('reports.layout.stepsFlow')}
        </Typography>

        {loadingUsageCheck ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>{t('reports.layout.checkingUsageLimits')}</Typography>
          </Box>
        ) : (
          /* Mostrar mensaje de límite alcanzado si canPerform es false */
          usageCheck && !usageCheck.canPerform && !reportId ? (
            renderUsageLimitMessage()
          ) : (
            <MakerStepper
              pasos={pasos}
              dynamicSteps={dynamicSteps}
              pasoFinal={pasoFinal}
              positions={positions}
              loadingPositions={loadingPositions}
              selectedPosition={selectedPosition}
              onPositionChange={handlePositionChange}
              candidateData={candidateData}
              onCandidateChange={setCandidateData}
              contexts={contexts}
              onContextChange={handleContextChange}
              localFiles={localFiles}
              onFilesChange={handleFilesChange}
              skipped={skipped}
              onSkippedChange={handleSkippedChange}
              activeReport={activeReport}
              gptTask={gptTask}
              gptProcessing={gptProcessing}
              sending={sending}
              uploadedCount={uploadedCount}
              totalFiles={totalFiles}
              progressPercent={progressPercent}
              allDone={allDone}
              t={t}
              authToken={authToken}
              user={user}
              project={project}
              organization={organization}
              language={language}
              reportId={reportId}
              navigate={navigate}
              showAlert={showAlert}
              onCreateReport={handleCreateReport}
              onProcessGptTask={processGptTask}
              onRetryGptTask={retryGptTask}
              isInFinalState={isInFinalState}
              downloadFile={downloadFile}
              downloadWordDocument={downloadWordDocument}
              handleDynamicStepsChange={handleDynamicStepsChange}
              isReportCompleted={isInFinalState(activeReport?.status)}
              onRefreshPositions={handleRefreshPositions}
            />)
        )}
      </Grid>
    </Grid>
  );
}